import React, { useEffect, useRef, useState } from 'react';
import { FaPlayCircle } from 'react-icons/fa';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselFullscreen,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@repo/ui-components/shadcn/Carousel';
import Video from './Video';

interface ProductMediaCarouselProps {
  media: {
    id: string;
    image: {
      src: string;
      altText: string;
    };
    width: number;
    height: number;
    __typename: 'ShopifyImage' | 'ShopifyVideo' | 'ShopifyExternalVideo';
    sources: {
      url: string;
      type: string;
    }[];
    embeddedUrl: string;
    preview: {
      image: {
        src: string;
        altText: string;
      };
    };
  }[];
}

export default function ProductMediaCarousel({
  media,
}: Readonly<ProductMediaCarouselProps>) {
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [announcement, setAnnouncement] = useState('');
  const [currentSlideIsVideo, setCurrentSlideIsVideo] = useState(false);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const fullScreenButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const index = carouselApi?.selectedScrollSnap();

    setCurrentSlideIsVideo(
      media[index]?.__typename === 'ShopifyVideo' ||
        media[index]?.__typename === 'ShopifyExternalVideo'
    );
  }, [carouselApi, currentSlideIsVideo, media, selectedIndex]);

  useEffect(() => {
    if (!carouselApi) return;

    const onSelect = () => {
      const index = carouselApi.selectedScrollSnap();
      setSelectedIndex(index);
      setAnnouncement(`Slide ${index + 1} of ${media?.length}`);
      thumbnailRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    };

    carouselApi.on('select', onSelect);
  }, [carouselApi, media]);

  const handleThumbnailClick = (index: number) => {
    carouselApi.scrollTo(index);
    setSelectedIndex(index);
    setAnnouncement(`Slide ${index + 1} of ${media?.length}`);
    thumbnailRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  const preventEmblaDrag = {
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
  };

  return (
    <section className="w-full" aria-label="Product media carousel">
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {announcement}
      </div>

      <Carousel
        className="h-full w-full relative"
        defaultValue={0}
        orientation="horizontal"
        setApi={setCarouselApi}
        opts={{
          containScroll: false,
          loop: true,
        }}
      >
        {media?.length > 1 && (
          <>
            <CarouselPrevious
              ref={prevButtonRef}
              className="absolute left-2 top-6 z-[1] -translate-y-1/2 transform"
              {...preventEmblaDrag}
              id="previous-slide-button"
              onKeyDown={e => {
                if (
                  typeof document === 'undefined' ||
                  !document.fullscreenElement
                )
                  return;

                // Forward tab (loop back to last focusable element)
                if (
                  e.key === 'Tab' &&
                  e.shiftKey &&
                  fullScreenButtonRef.current
                ) {
                  e.preventDefault();
                  fullScreenButtonRef.current.focus();
                }
              }}
            />
            <CarouselNext
              className="absolute left-12 top-6 z-[1] -translate-y-1/2 transform"
              {...preventEmblaDrag}
            />
          </>
        )}

        <CarouselContent>
          {media?.map((item, index) => (
            <CarouselItem
              key={item.id}
              style={{
                width: item.width ? item.width : '100%',
                height: item.height,
              }}
              id={`panel-${index}`}
              role="tabpanel"
              aria-labelledby={`tab-${index}`}
              aria-hidden={selectedIndex !== media.indexOf(item)}
            >
              {(() => {
                if (
                  item.__typename === 'ShopifyVideo' ||
                  item.__typename === 'ShopifyExternalVideo'
                ) {
                  return (
                    <div className="bg-black h-full flex items-center justify-center rounded overflow-hidden">
                      {item.sources?.length > 0 ? (
                        <Video
                          url={item.sources[0].url}
                          poster={item.preview?.image?.src}
                          activateFocus={currentSlideIsVideo}
                        />
                      ) : (
                        <Video
                          url={item.embeddedUrl}
                          poster={item.preview?.image?.src}
                          activateFocus={currentSlideIsVideo}
                        />
                      )}
                    </div>
                  );
                }

                if (item.image?.src) {
                  return (
                    <div className="flex h-full items-center justify-center overflow-hidden">
                      <img
                        src={`${item.image.src}&width=784`}
                        alt={item.image?.altText || 'Product image'}
                        width="784"
                        className="h-full max-h-screen object-scale-down"
                      />
                    </div>
                  );
                }

                return null;
              })()}
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselFullscreen
          ref={fullScreenButtonRef}
          className="bottom-2 right-2"
          onKeyDown={e => {
            if (typeof document === 'undefined' || !document.fullscreenElement)
              return;

            // Forward tab (loop back to first focusable element)
            if (e.key === 'Tab' && !e.shiftKey && prevButtonRef.current) {
              e.preventDefault();
              prevButtonRef.current.focus();
            }
          }}
        />
      </Carousel>

      {media?.length > 1 && (
        <div
          className="mt-4 flex justify-center gap-2 md:hidden"
          aria-label="Slide navigation dots"
          role="tablist"
        >
          {media.map((item, index) => (
            <button
              type="button"
              key={item.id}
              onClick={() => handleThumbnailClick(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-3 w-3 rounded-full transition-colors ${
                selectedIndex === index ? 'bg-primary' : 'bg-border'
              }`}
              aria-selected={selectedIndex === index}
              role="tab"
            />
          ))}
        </div>
      )}

      {media?.length > 1 && (
        <div
          className="mt-4 hidden md:flex gap-2 overflow-x-auto px-2 pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          aria-label="Media thumbnails"
          role="tablist"
        >
          <style>
            {`
              div::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          {media.map((item, index) => (
            <div key={item.id}>
              <button
                id={`tab-${item.id}`}
                type="button"
                role="tab"
                aria-controls={`panel-${item.id}`}
                aria-selected={selectedIndex === index}
                aria-label={`Go to slide ${index + 1}`}
                tabIndex={0}
                onClick={() => handleThumbnailClick(index)}
                ref={el => {
                  thumbnailRefs.current[index] = el;
                }}
                className={`relative h-24 w-24 shrink-0 rounded border-2 transition-all ${
                  selectedIndex === index ? 'border-primary' : ''
                }`}
              >
                {(() => {
                  if (
                    (item.__typename === 'ShopifyVideo' ||
                      item.__typename === 'ShopifyExternalVideo') &&
                    item.preview?.image?.src
                  ) {
                    return (
                      <div className="relative w-full h-full">
                        <img
                          src={item.preview.image.src}
                          alt={`Thumbnail ${index + 1}`}
                          className="object-cover w-full h-full overflow-hidden rounded"
                        />
                        <span
                          className="absolute inset-0 flex items-center justify-center"
                          aria-hidden="true"
                        >
                          <FaPlayCircle className="text-white text-2xl" />
                        </span>
                      </div>
                    );
                  }

                  if (item.image?.src) {
                    return (
                      <img
                        src={`${item.image.src}&width=100`}
                        alt={`Thumbnail ${index + 1}`}
                        className="object-cover w-full h-full overflow-hidden"
                      />
                    );
                  }

                  return null;
                })()}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
